import NavLink from '../NavHead/NavLink';
import Logo from '../../../assets/Imagess/logo/logo.svg';
import styles from './SideBar.module.scss';
import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { usePageAccess } from '../../../Context/PageAccessContext';
import { ownerOfRow } from '../../../services/pageAccess';
import appIcon from '../../../assets/Imagess/inActive-appicon.svg';
// `contactIcon` is live again: التواصل was restored 2026-11-03, together with
// the website form on /academics/contact-us.
import contactIcon from '../../../assets/Imagess/emailicon.svg';
// `settingIcon` is live again: إعدادات الصفحة الرئيسية was restored 2026-10-14.
import settingIcon from '../../../assets/Imagess/settingIcon.svg';

/**
 * PUBLIC PATHS, NOT IMPORTS — and deliberately so.
 *
 * `GetPageAccess` addresses every icon it serves as `/Icons/<file>.svg`, resolved
 * by `NavLink` against the CMS's own origin (there is no `homepage` in
 * package.json, so the app is served from the root and the leading slash is
 * correct). These two rows are client stopgaps that the API will eventually
 * serve itself — see CLIENT_LINKS below — and when it does it will send exactly
 * these two strings.
 *
 * Importing the files from `assets/Imagess` instead would hash the filenames at
 * build time, so the stopgap and the API row would render DIFFERENT icons for
 * the same screen, and the swap-over would be a visible change rather than a
 * silent one. Pointing both at the same public path makes the handover invisible.
 *
 * Both files were drawn for this rail: white, outline-only, transparent ground,
 * stroke 1.6 on a 24 grid. The ground matters — the active row re-tints the icon
 * with `filter: brightness(0) …` (NavLink.module.scss), which would turn any
 * filled backing plate into a solid green block.
 *
 * They replace two BORROWED icons that said the wrong thing: campaignVector.svg
 * (a #8D90A5 grey circular badge, so it rendered dim against the green and did
 * not re-tint cleanly) and usersInactive.svg, which is the people glyph the
 * المستخدمون row already uses — two different screens with one icon.
 */
const announcementIcon = '/Icons/announcementIcon.svg';
const visitorIcon = '/Icons/visitorIcon.svg';

/**
 * Screens the client knows about that the backend's page-access response does
 * not list yet.
 *
 * The sidebar is built from the API, so a new screen is invisible in the menu
 * until the backend serves it. These entries make those routes reachable today.
 *
 * `withClientLinks` de-duplicates by path, so each one disappears on its own
 * the moment the API starts returning it — nothing to remember to remove, and
 * no duplicate row in the meantime.
 *
 * Tracked as ask D in
 * DPA-FE `docs/Controlled/MainMenu/09-backend-request.md`.
 */
const CLIENT_LINKS = [
  /**
   * RESTORED 2026-11-03, together with the website form.
   *
   * Parked on 2026-09-28 alongside the contact form on /academics/contact-us;
   * both halves are back. The record for the pair is
   * DPA-FE `docs/Deployment/laterOnContactUsePublishing.md`.
   *
   * A GROUP, not a leaf — the inbox and the recipient list that feeds it are
   * two screens for one subject, and `/api/Auth/GetPageAccess` serves neither.
   * If the API later returns `/home/contact-messages` as a top-level row,
   * rule 2 in `withClientLinks` pulls it in here rather than leaving the same
   * screen in two places.
   */
  {
    name: 'التواصل',
    path: '',
    icon: contactIcon,
    nested: [
      {
        name: 'رسائل التواصل',
        path: '/home/contact-messages',
        icon: contactIcon,
      },
      {
        name: 'إعدادات جهات التواصل',
        path: '/home/contact-settings',
        icon: settingIcon,
      },
    ],
  },
  {
    // The announcement pop-up (الإعلانات). `/api/Auth/GetPageAccess` does not
    // serve this row yet — requested as §X1 of
    // DPA-FE docs/Announcements&visitors/02-backend-api-request.md, along with
    // which roles should receive it and the icon file to use.
    //
    // A LEAF, not a group: it is one screen, and the create/edit form is
    // reached from it rather than from the menu. `withClientLinks` drops this
    // entry on its own the day the API starts returning the path, so there is
    // nothing here to remember to remove.
    name: 'الإعلانات',
    path: '/home/announcements',
    icon: announcementIcon,
    nested: [],
  },
  {
    // Visitor count (عدد الزوار) — the figures and the footer-display switch.
    // Same `GetPageAccess` gap, same self-removing stopgap.
    name: 'عدد الزوار',
    path: '/home/visitor-counter',
    icon: visitorIcon,
    nested: [],
  },
  {
    // The home-page group requested for the controlled-navigation work.
    // `محتوى الصفحة الرئيسية` (/home/HomeSlider) already exists as a TOP-LEVEL
    // row in the API response; rule 2 in `withClientLinks` moves it in here
    // rather than leaving the operator with the same screen in two places.
    name: 'الصفحة الرئيسية',
    path: '',
    icon: appIcon,
    nested: [
      {
        name: 'محتوى الصفحة الرئيسية',
        path: '/home/HomeSlider',
        icon: appIcon,
      },
      // SHOWN AGAIN 2026-10-14, by request, on the V10 line.
      //
      // It has now moved four times — hidden 2026-09-22 while the screen was
      // being built, shown 2026-09-30, hidden 2026-10-13 for the V09 release,
      // shown again today — and the route never went away through any of it.
      // If it is hidden a fifth time, comment this entry out AND the
      // `settingIcon` import at the top of the file: the import is otherwise
      // unused, and `no-unused-vars` is a BUILD FAILURE under CI.
      //
      // Restoring it also restores the GROUP, which is the point rather than a
      // side effect. With one child left, «الصفحة الرئيسية» duplicated
      // `/home/HomeSlider` — a path the API already serves as a TOP-LEVEL
      // row — so rule 1 in `withClientLinks` dropped the whole client entry and
      // HomeSlider sat on its own. Two children, so both rows appear together
      // under «الصفحة الرئيسية» again.
      {
        name: 'إعدادات الصفحة الرئيسية',
        path: '/home/home-settings',
        icon: settingIcon,
      },
    ],
  },
];

/** Every path a link occupies — itself and its children. */
const pathsOf = (link) => [
  link?.path,
  ...(Array.isArray(link?.nested) ? link.nested.map((n) => n?.path) : []),
];

/**
 * Drop the parts of a client link that the BACKEND now owns.
 *
 * THIS IS THE RULE THAT KEEPS THE MENU HONEST. A client link is injected for
 * everybody, because it exists outside the permission system. The moment a
 * screen becomes assignable, injecting it would put a row in the menu that the
 * route gate then refuses — the operator clicks their own menu and is told no.
 *
 * So: if `GetRolePages` offers the screen, the API alone decides whether this
 * operator sees it. `announcements` and `visitor-counter` crossed that line on
 * 2026-10-21 and now disappear from here on their own.
 *
 * @param {object} link
 * @param {Set<string>} assignable owners the backend offers
 * @returns {object|null} the link, a trimmed copy of it, or null
 */
const withoutAssignable = (link, assignable) => {
  if (!link) return null;

  if (!isEmpty(link.nested)) {
    const nested = link.nested.filter(
      (child) => !assignable.has(ownerOfRow(child))
    );
    // A group with nothing left in it is not a heading, it is clutter.
    return isEmpty(nested) ? null : { ...link, nested };
  }

  return assignable.has(ownerOfRow(link)) ? null : link;
};

/**
 * Merge the client-known screens into whatever the API returned.
 *
 * Three rules, in order:
 *   0. Anything the backend can grant is the backend's to decide — see
 *      `withoutAssignable` above.
 *   1. A client link whose paths the API already serves is dropped entirely —
 *      the API's own entry wins, so this file needs no edit when the backend
 *      catches up.
 *   2. A client GROUP absorbs any top-level API row that duplicates one of its
 *      children, so `محتوى الصفحة الرئيسية` moves under `الصفحة الرئيسية`
 *      instead of appearing twice.
 */
const withClientLinks = (links, assignable = new Set()) => {
  const list = Array.isArray(links) ? links : [];

  const additions = CLIENT_LINKS.map((candidate) =>
    withoutAssignable(candidate, assignable)
  ).filter((candidate) => {
    if (!candidate) return false;
    const candidatePaths = pathsOf(candidate).filter(Boolean);
    if (isEmpty(candidatePaths)) return false;
    // Keep the candidate unless the API already covers every path it adds.
    return !candidatePaths.every((path) =>
      list.some((link) => pathsOf(link).includes(path))
    );
  });

  if (isEmpty(additions)) return list;

  const absorbed = new Set(
    additions.flatMap((addition) =>
      (addition.nested ?? []).map((child) => child.path).filter(Boolean)
    )
  );

  // Only absorb a LEAF row. A group the API owns keeps its own children.
  const kept = list.filter(
    (link) => !(isEmpty(link?.nested) && absorbed.has(link?.path))
  );

  return [...kept, ...additions];
};

const SideBar = () => {
  /**
   * The menu and the route gate now read the SAME two lists, from
   * `Context/PageAccessContext`.
   *
   * This component used to fetch `GetPageAccess` itself, and `MainRoutes`
   * fetched it a second time for a permission check it then threw away — two
   * requests per load, and nothing stopping the menu and the router from
   * disagreeing about what the operator holds. The seeding from `localStorage`,
   * the `nested: []` workaround and the fall back to the stored tree on failure
   * all moved into the provider unchanged; see the notes there.
   */
  const { pages, assignable } = usePageAccess();

  const navPageLinks = useMemo(
    () => withClientLinks(pages, assignable),
    [pages, assignable]
  );

  return (
    <div className={styles['sidebar']}>
      <div className={styles['sidebar-container']}>
        <img src={Logo} alt='logo' className={styles.logo} />
        {!isEmpty(navPageLinks) && <NavLink navPageLinks={navPageLinks} />}
      </div>
    </div>
  );
};

export default SideBar;
